/** @jsxImportSource @emotion/react */
import { useDispatch, useSelector } from "react-redux";
import { IAppState } from "../../core/redux/IAppState";
import { getError, getLoading } from "../../core/redux/api/module";
import { ACTION_TYPE } from "../../core/redux/actionType";
import { getWeeklyVisitorDataReportsAction } from "./getWeeklyVisitorReportsAction";
import { useEffect, useMemo, useState, useCallback } from "react";
import { getResource } from "../../language/resources/getResource";
import { showSnackbarAction } from "../../snackbar/snackbarAction";
import { WeeklyVisitorDataReport } from "../models";
import { css } from "@emotion/react";
import { Loader } from "../../forms/elements/loader/Loader";
import ErrorView from "../../forms/ErrorView";

const reportLoader = css`
  width: 80px;
  height: 80px;
  padding: 0px;
  margin-top: 25px;
  margin-bottom: 10px;
`;

const errorView = css`
  margin-top: 25px;
  padding-top: 25px;
`;

const linkButtonStyles = css`
  /* Link look */
  color: #000;
  text-decoration: underline;
  font-size: 1.125rem;
  cursor: pointer;
  display: inline;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 24rem;

  &:focus {
    outline: 2px solid #0b6cff;
    outline-offset: 2px;
  }
  &:hover {
    text-decoration-thickness: 2px;
  }
  &:active {
    opacity: 0.8;
  }
`;

const disabledLinkStyles = css`
  pointer-events: none;         /* blocks mouse clicks */
  opacity: 0.6;
  cursor: not-allowed;
  &:hover {
    text-decoration: none;
    filter: none;
  }
  &:focus {
    outline: none;
  }
`;

const listStyles = css`
  list-style: none;
  padding: 0;
  margin-left: 25px;
  margin-top: 30px;

  & > li::marker {
    content: none;
  }
`;

const listItemStyles = css`
  list-style: none;
  padding-top: 20px;
  padding-left: 16px;
`;

const DEFAULT_LINK_TIMEOUT = 2000;

/** Per-key lock for N ms */
function useLinkLock(timeoutMs = DEFAULT_LINK_TIMEOUT) {
  const [lockedByKey, setLockedByKey] = useState<Record<string, boolean>>({});
  const isLocked = useCallback((key: string) => !!lockedByKey[key], [lockedByKey]);

  const lock = useCallback((key: string) => {
    setLockedByKey(prev => ({ ...prev, [key]: true }));
    const id = window.setTimeout(() => {
      setLockedByKey(prev => ({ ...prev, [key]: false }));
    }, timeoutMs);
    return () => window.clearTimeout(id);
  }, [timeoutMs]);

  return { isLocked, lock };
}

const WeeklyVisitorReports: React.FC = () => {
  const dispatch = useDispatch();
  const selectedResort = useSelector((state: IAppState) => state.selectedResort);
  const numberReports = useSelector(
    (state: IAppState) => state.resortToolsConfig.weeklyVisitorReportLimit
  );
  const weeklyVisitorDataReportsRaw = useSelector(
    (state: IAppState) => state.weeklyVisitorDataReports
  );

  const isLoadingReports = useSelector(
    (state: IAppState) =>
      !!getLoading(
        state,
        ACTION_TYPE.GET_WEEKLY_VISITOR_DATA_REPORTS_REQUEST,
        ACTION_TYPE.GET_WEEKLY_VISITOR_DATA_REPORTS_REQUEST
      )
  );

  const errorLoadingReports = useSelector((state: IAppState) =>
    getError(
      state,
      ACTION_TYPE.GET_WEEKLY_VISITOR_DATA_REPORTS_ERROR,
      ACTION_TYPE.GET_WEEKLY_VISITOR_DATA_REPORTS_ERROR
    )
  );

  const resources = useSelector((state: IAppState) => state.locale);
  const showError = useCallback((errorMsg: string) => dispatch(showSnackbarAction(errorMsg, true)), [dispatch]);

  useEffect(() => {
    if (errorLoadingReports) {
      showError(
        getResource(
          resources,
          "REPORTS_WEEKLY_VISITOR_ERROR_LOADING",
          "reportName"
        )
      );
    }
  }, [errorLoadingReports, resources, showError]);

  useEffect(() => {
      dispatch(
        getWeeklyVisitorDataReportsAction(
          selectedResort.resortAbbreviation,
          numberReports
        )
      );
  }, [dispatch, numberReports, selectedResort.resortAbbreviation]);

  const sortedReports = useMemo(() => {
    const items: WeeklyVisitorDataReport[] = weeklyVisitorDataReportsRaw ?? [];
    return items.slice().sort((a, b) => {
      const da = new Date(a.createDate).getTime();
      const db = new Date(b.createDate).getTime();
      return da - db;
    });
  }, [weeklyVisitorDataReportsRaw]);

  const { isLocked, lock } = useLinkLock(DEFAULT_LINK_TIMEOUT);

  return (
    <div>
      {/* Loading */}
      {isLoadingReports && <Loader rootCss={reportLoader} />}

      {/* Empty state */}
      {!isLoadingReports && sortedReports.length === 0 && (
        <div css={errorView}>
          <ErrorView
            error={getResource(resources, "REPORTS_WEEKLY_VISITOR_REPORT_NONE_FOUND")}
          />
        </div>
      )}

      {/* Reports list */}
      {sortedReports.length > 0 && (
        <ul css={listStyles}>
          {sortedReports.map((r) => {
            const key = r.uri;
            const locked = isLocked(key);

            return (
              <li key={`${r.uri}-${r.fileName}`} css={listItemStyles}>
                <a
                  href={r.uri}
                  css={[linkButtonStyles, locked && disabledLinkStyles]}
                  title={r.fileName}
                  download={r.fileName}
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    if (locked) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    // Start lock and let the default navigation/download happen
                    lock(key);
                  }}
                  aria-disabled={locked}
                  tabIndex={locked ? -1 : 0} // prevent keyboard focus when locked
                >
                  {r.fileName}
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default WeeklyVisitorReports;
